// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Charts } from '@/_shared/components/Charts';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { Routes } from '@/_shared/constants/routes';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployeesStatsCall } from '@/people/state';
import { getSurveysCampaignsCall, useCurrentCampaign } from '../../state';
import { ReminderDialog } from '../ReminderDialog';
import { SendSurvey } from '../SendSurvey';
import { SetVisibilityDialog } from '../SetVisibilityDialog';

type Props = {
  setTrigger?: (value: boolean) => void;
  trigger?: boolean;
};

export const CampaignsList: FC<Props> = ({ trigger, setTrigger }) => {
  const t = useTranslations('surveys.campaign');
  const tShared = useTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCompleted, setIsCompleted] = useState(false);

  const {
    isOpen: isOpenSendSurvey,
    closeDialog: closeDialogSendSurvey,
    openDialog: openDialogSendSurvey,
  } = useDialog('sendSurvey');

  const {
    isOpen: isOpenReminderSurvey,
    closeDialog: closeDialogReminderSurvey,
    openDialog: openDialogReminderSurvey,
  } = useDialog('reminderSurvey');

  const {
    openDialog: openDialogSetVisibilityDialog,
    isOpen: isOpenSetVisibilityDialog,
    closeDialog: closeDialogSetVisibilityDialog,
  } = useDialog('setVisibilityDialog');

  const {
    loadingStats,
    errorStats,
    successStats,
    surveyId,
    currentCampaignId,
    currentSurveyName,
    currentVersion,
    setCurrentVersion,
    currentCampaignStats,
    isVisible,
  } = useCurrentCampaign();

  const { completed } = getEmployeesStatsCall.hook();

  const { watch } = useForm();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fieldValues = watch();

  const gapXs = useThemeValue('space', 'xs');

  const { genericResponsiveValue } = useResponsiveStyle();

  const { execute: executeGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  const goToDetails = useCallback(() => {
    if (surveyId && currentCampaignId) {
      navigate(
        Routes.SurveyDetailsStats.replace(':surveyId', surveyId).replace(
          ':campaignId',
          currentCampaignId,
        ),
      );
    }
  }, [currentCampaignId, navigate, surveyId]);

  const goToPeople = useCallback(() => {
    navigate(Routes.People);
  }, [navigate]);

  const coveragePercentage =
    completed > currentCampaignStats.totalInvitation
      ? (currentCampaignStats.totalInvitation / completed) * 100
      : 100;

  const onCustomClose = useCallback(() => {
    if (isCompleted) {
      executeGetSurveysCampaigns({});
      setIsCompleted(false);
    }
    if (setTrigger) {
      setTrigger(false);
    }
    closeDialogSetVisibilityDialog();
  }, [isCompleted, closeDialogSetVisibilityDialog, setTrigger, executeGetSurveysCampaigns]);

  useEffect(() => {
    if (fieldValues?.templates?.value && currentVersion?.id !== fieldValues.templates.value) {
      setCurrentVersion({
        id: fieldValues.templates.value,
        name: fieldValues.templates.label,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.templates, currentVersion]);

  useEffect(() => {
    if (trigger) {
      openDialogSetVisibilityDialog();
    }
  }, [openDialogSetVisibilityDialog, trigger]);

  useEffect(() => {
    if (location.state?.dialog === 'visibility') {
      openDialogSetVisibilityDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (!loadingStats && !errorStats && successStats) {
    return (
      <>
        <Box>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            alignItems={genericResponsiveValue({
              breakPoints: { desktop: 'center' },
              defaultValue: 'flex-start',
            })}
            justifyContent='space-between'
          >
            <Box>
              <Typography variant='h2' mb='xxxs'>
                {currentSurveyName}
              </Typography>
              <Typography variant='p2/light'>{t('description')}</Typography>
            </Box>
            {/* TODO: using it post-MVP <Box>
              <Form.FormDropdown
                control={control}
                name='templates'
                items={templateItems || []}
                labelStyle={{ color: 'white', mb: 'xxs' }}
                data-testid='dropdown-select-campaign'
                optionTestId='dropdown-select-campaign-option'
                placeholder={currentVersion?.name || t('selectVersion')}
              />
            </Box> */}
          </Box>
          <Box
            display='grid'
            gridTemplateColumns={genericResponsiveValue({
              breakPoints: { desktop: '1fr 1fr' },
              defaultValue: '1fr',
            })}
            style={{ gap: gapXs }}
            mt='l'
          >
            <Card>
              <Typography variant='h3'>{t('results.title')}</Typography>
              <Typography variant='p2/light'>
                {t('results.description', { total: currentCampaignStats.totalInvitation })}
              </Typography>
              <Charts.HalfPieChartComponent
                data={[
                  { name: t('results.completed'), value: currentCampaignStats.completedSurveys },
                  {
                    name: t('results.notCompleted'),
                    value:
                      currentCampaignStats.totalInvitation - currentCampaignStats.completedSurveys,
                  },
                ]}
                colors={['primary', 'white']}
                centerValue={`${((currentCampaignStats.completedSurveys / currentCampaignStats.totalInvitation) * 100).toFixed(0)}%`}
                centerLabelValue={`${t('results.completionRate')}`}
              />
              <Box
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'row' },
                  defaultValue: 'column',
                })}
                display='flex'
                justifyContent='center'
                mt='m'
                mb='l'
                width={genericResponsiveValue({
                  breakPoints: { desktop: 'auto' },
                  defaultValue: '100%',
                })}
                alignItems={genericResponsiveValue({
                  breakPoints: { desktop: 'center' },
                  defaultValue: 'center',
                })}
              >
                <Box
                  mb={genericResponsiveValue({
                    breakPoints: { desktop: 'none' },
                    defaultValue: 'xs',
                  })}
                  display='flex'
                  alignItems='center'
                >
                  {currentCampaignStats.totalInvitation - currentCampaignStats.completedSurveys >
                    0 && (
                    <>
                      <Button
                        mr={genericResponsiveValue({
                          breakPoints: { desktop: 'xxs' },
                          defaultValue: 'none',
                        })}
                        variant={
                          currentCampaignStats.completedSurveys > 0 ? 'link/primary' : 'primary'
                        }
                        onClick={openDialogReminderSurvey}
                      >
                        {t('results.sendReminder')}
                      </Button>
                      {currentCampaignStats.completedSurveys > 0 && <Svg name='send' size='xs' />}
                    </>
                  )}
                </Box>
                {currentCampaignStats.completedSurveys > 0 && (
                  <Button onClick={goToDetails} ml='xs' variant='primary'>
                    {t('results.viewResults')}
                  </Button>
                )}
              </Box>
              {currentCampaignStats.completedSurveys < 1 && (
                <AlertBadge
                  type='transparent'
                  iconName='lightBulb'
                  label={t('results.alertLabel')}
                />
              )}
            </Card>
            <Card>
              <Typography variant='h3'>{t('coverage.title')}</Typography>
              <Typography variant='p2/light'>
                {t('coverage.description', { total: completed || 0 })}
              </Typography>
              <Charts.HalfPieChartComponent
                data={[
                  {
                    name: t('coverage.totalSended'),
                    value: currentCampaignStats.totalInvitation,
                  },
                  {
                    name: t('coverage.notSended'),
                    value:
                      completed > currentCampaignStats.totalInvitation
                        ? completed - currentCampaignStats.totalInvitation
                        : 0,
                  },
                ]}
                colors={['primary', 'white']}
                centerValue={`${coveragePercentage.toFixed(0)}%`}
                centerLabelValue={`${t('coverage.completionRate')}`}
              />
              {completed - currentCampaignStats.totalInvitation > 0 && (
                <>
                  <Box display='flex' justifyContent='center' mt='m' mb='l'>
                    <Button onClick={openDialogSendSurvey} ml='xs' variant='primary'>
                      {t('coverage.sendSurvey')}
                    </Button>
                  </Box>{' '}
                  <AlertBadge
                    type='transparent'
                    iconName='lightBulb'
                    label={
                      <Typography variant='p2/light'>
                        <RenderLocalesWithComponents
                          text={t('coverage.alertLabel', {
                            downloadFile: '!{CTA}!',
                          })}
                          placeholders={{
                            '!{CTA}!': (
                              <Typography
                                as='span'
                                variant='p2/light'
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                onClick={goToPeople}
                              >
                                {t('coverage.ctaLabel')}
                              </Typography>
                            ),
                          }}
                        />
                      </Typography>
                    }
                  />
                </>
              )}
            </Card>
          </Box>
          <Card
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            mt='s'
            alignItems={genericResponsiveValue({
              breakPoints: { desktop: 'center' },
              defaultValue: 'flex-start',
            })}
            backgroundColor='primary'
            display='flex'
          >
            <Box
              mr={genericResponsiveValue({
                breakPoints: { desktop: 'xs' },
                defaultValue: 'none',
              })}
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'xs',
              })}
            >
              <Svg name={isVisible ? 'eyeOn' : 'eyeOff'} size='s' />
            </Box>
            <Box flex='2'>
              <Typography variant='h4'>
                {isVisible ? t('status.visible.title') : t('status.notVisible.title')}
              </Typography>
              <Typography variant='p2/light'>
                {isVisible ? t('status.visible.description') : t('status.notVisible.description')}
              </Typography>
            </Box>
            <Box
              flex='1'
              mt={genericResponsiveValue({
                breakPoints: { desktop: 'none' },
                defaultValue: 'xs',
              })}
            >
              <Button
                onClick={openDialogSetVisibilityDialog}
                style={{ margin: '0 0 0 auto' }}
                variant='positive'
              >
                {t('status.cta')}
              </Button>
            </Box>
          </Card>
        </Box>
        <SendSurvey
          currentCampaignId={currentCampaignId}
          currentTemplateId={surveyId}
          currentTemplateName={currentSurveyName}
          currentVersionId={currentVersion?.id}
          isOpen={isOpenSendSurvey}
          closeDialog={closeDialogSendSurvey}
          isExistingCampaign
          isVisible={isVisible}
        />
        <ReminderDialog
          currentCampaignId={currentCampaignId}
          isOpen={isOpenReminderSurvey}
          closeDialog={closeDialogReminderSurvey}
          total={currentCampaignStats.totalInvitation}
          completed={currentCampaignStats.completedSurveys}
          pending={currentCampaignStats.totalInvitation - currentCampaignStats.completedSurveys}
          assessmentName={currentSurveyName}
          lastSendDate={currentCampaignStats?.lastRemindAt}
        />
        <SetVisibilityDialog
          isOpen={isOpenSetVisibilityDialog}
          closeDialog={onCustomClose}
          isVisible={isVisible}
          currentCampaignId={currentCampaignId}
          totalCompletedResponses={currentCampaignStats.completedSurveys}
          setIsCompleted={setIsCompleted}
          setTrigger={setTrigger}
        />
      </>
    );
  }

  return null;
};
